/**
 * Super Fast Delivery Date Handler for Shopify Theme
 * Optimized for instant performance with no loading states
 * 
 * Features:
 * - Standard delivery calculation (Fast Delivery By)
 * - Range delivery calculation (Expected Delivery X - Y)
 * - $1 Overnight shipping eligibility (Thursday before 9 AM PST only)
 * - Thursday morning exception (9 AM PST)
 * - All times in PST/PDT
 * - Configurable cutoff times per template type
 * - NEW: 'ddd, MMMM Do' format for "Tue, August 26th"

 * 
 * CUTOFF TIMES:
 * - Standard cutoff: 7 PM PST (orders after 7 PM treated as next day)
 * - Thursday cutoff: 9 AM PST (before = 1 day, after = 5-7 days)
 * 
 * DELIVERY LOGIC:
 * - Sunday: 2-4 days
 * - Monday: 2-4 days  
 * - Tuesday: 2-3 days
 * - Wednesday: 2 days (Fast Delivery By)
 * - Thursday: Before 9 AM = 1 day ($1 Overnight Shipping), After 9 AM = 5-7 days (Expected Delivery)
 * - Friday: 4-6 days
 * - Saturday: 3-5 days
 * 
 * ================================================================================
 * COMPREHENSIVE REQUIREMENTS TABLE - SINGLE SOURCE OF TRUTH
 * ================================================================================
 * 
 * File Path: assets/delivery-date-handler.js
 * Page/Component: All Widgets (Product Pages, Cart Drawer, etc.)
 * Primary Logic/Purpose: To provide a single, consistent delivery estimate across the entire website
 * 
 * CUTOFF TIME: 7 PM PST. Orders placed after this time are treated as being placed on the next calendar day.
 * 
 * SPECIAL CASES:
 * • Thursday's 9 AM PST Rule: Thursday has a unique, earlier cutoff. Orders before this time get a much faster delivery
 * • Display Format: Logic must show "Fast Delivery By [Earliest Day]" for single-day estimates and "Expected Delivery [Earliest Day] - [Latest Day]" for ranges.
 * 
 * RULE: if widget says one day, then its the earliest day
 * RULE: if widget shows a range of days, then it shows the first and last day
 * EXAMPLES:
 * - Fast Delivery By X = first day
 * - Expected Delivery X - Y = first and last day
 * 
 * DAY-BY-DAY LOGIC:
 * 
 * SUNDAY LOGIC:
 * - 2-4 Days
 * - Expected Delivery: For an order placed on Sunday, the estimate is Tuesday - Thursday.
 * 
 * MONDAY LOGIC:
 * - 2-4 Days
 * - Expected Delivery: For an order placed on Monday, the estimate is Wednesday - Friday.
 * 
 * TUESDAY LOGIC:
 * - 2-3 Days
 * - Expected Delivery: For an order placed on Tuesday, the estimate is Thursday - Friday.
 * 
 * WEDNESDAY LOGIC:
 * - 2 Days
 * - Fast Delivery By: For an order placed on Wednesday, the estimate is Friday.
 * 
 * THURSDAY LOGIC:
 * - (Before 9 AM PST): 1 Day
 *   Fast Delivery By: For an order placed before the 9 AM cutoff, the estimate is Friday.
 * - (After 9 AM PST): 5-7 Days
 *   Expected Delivery: For an order placed after the 9 AM cutoff, the estimate is the following Tuesday - Thursday.
 * 
 * FRIDAY LOGIC:
 * - 4-6 Days
 * - Expected Delivery: For an order placed on Friday, the estimate is the following Tuesday - Thursday.
 * 
 * SATURDAY LOGIC:
 * - 3-5 Days
 * - Expected Delivery: For an order placed on Saturday, the estimate is the following Tuesday - Thursday.
 * 
 * OVERNIGHT SHIPPING:
 * - $1 Overnight shipping available ONLY on Thursday before 9 AM PST
 * - NOT available on Wednesday or any other day
 * - When available: Shows "$1 Overnight Shipping | Fast Delivery By [Date]"
 * 
 * ================================================================================
 * END REQUIREMENTS TABLE
 * ================================================================================
 */

class DeliveryDateHandler {
  constructor(options = {}) {
    this.options = {
      // Template type determines cutoff times
      templateType: options.templateType || 'standard', // standard, cart, test-member, build-a-box
      
      // Cutoff times based on template type
      standardCutoff: options.standardCutoff || 19, // 7 PM PST for main-product
      thursdayCutoff: options.thursdayCutoff || 9,  // 9 AM PST
      cartCutoff: options.cartCutoff || 9,          // 9 AM PST for cart overnight
      
      // Display options
      showCountdown: options.showCountdown !== false,
      showOvernight: options.showOvernight !== false,
      
      // Format options
      dateFormat: options.dateFormat || 'MM/DD',
      includeDayName: options.includeDayName !== false,
      
      // Shipping cost options
      overnightCost: options.overnightCost || 1,
      standardCost: options.standardCost || 7,
      

      
      ...options
    };
    
    // Pre-calculate common values for speed
    this.daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    this.months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    this.init();
  }

  init() {
    // Initialize timezone detection
    this.detectTimezone();
    
    // Set cutoff times based on template type
    this.setCutoffTimes();
  }

  /**
   * Set cutoff times based on template type
   */
  setCutoffTimes() {
    switch (this.options.templateType) {
      case 'test-member':
        this.options.standardCutoff = 19; // 7 PM PST
        this.options.thursdayCutoff = 9;  // 9 AM PST
        break;
      case 'build-a-box':
        this.options.standardCutoff = 19; // 7 PM PST
        this.options.thursdayCutoff = 9;  // 9 AM PST
        break;
      case 'cart':
        this.options.standardCutoff = 19; // 7 PM PST for cart logic
        this.options.thursdayCutoff = 9;  // 9 AM PST
        break;
      default: // standard/main-product
        this.options.standardCutoff = 19; // 7 PM PST
        this.options.thursdayCutoff = 9;  // 9 AM PST
        break;
    }
  }

  /**
   * Detect if we're in daylight saving time
   */
  detectTimezone() {
    const now = new Date();
    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);
    
    // Check if we're in PDT (UTC-7) or PST (UTC-8)
    this.isPDT = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset()) > now.getTimezoneOffset();
    this.pstOffset = this.isPDT ? -7 : -8; // PDT = -7, PST = -8
  }

  /**
   * Get current time in PST/PDT - OPTIMIZED
   */
  getCurrentTimePST() {
    const now = new Date();
    // Use more reliable timezone conversion
    const pstTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Los_Angeles"}));
    return pstTime;
  }

  /**
   * Calculate standard delivery date (Fast Delivery By) - OPTIMIZED
   * Used in main-product.liquid and most product templates
   * 
   * CUTOFF: 7 PM PST - orders after this time treated as next day
   * 
   * Logic: 
   * - Sun: 2 days (Tue)
   * - Mon: 2 days (Wed) 
   * - Tue: 2 days (Thu)
   * - Wed: 2 days (Fri) - Fast Delivery By
   * - Thu: Before 9 AM = 1 day (Fri), After 9 AM = 5 days (next Tue)
   * - Fri: 4 days (next Tue)
   * - Sat: 3 days (next Tue)
   */
  calculateStandardDelivery() {
    const currentTimePST = this.getCurrentTimePST();
    let currentDay = currentTimePST.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    let currentHour = currentTimePST.getHours();
    
    // Store original values before cutoff logic
    const originalDay = currentDay;
    const originalHour = currentHour;
    
    // Apply 7 PM PST cutoff - treat orders after 7 PM as next day
    let effectiveDate = new Date(currentTimePST);
    if (currentHour >= this.options.standardCutoff) {
      effectiveDate.setDate(effectiveDate.getDate() + 1);
      currentDay = effectiveDate.getDay();
    }
    
    // Thursday logic: before 9 AM PST = 1 day, after 9 AM PST = 5-7 days
    let daysToAdd = 0;
    if (currentDay === 4) {
      // For Thursday, we need to check the actual time, not the cutoff-adjusted time
      // If we got to Thursday from Wednesday after 7 PM, it's definitely after 9 AM
      const isActuallyThursday = originalDay === 4;
      const thursdayHour = isActuallyThursday ? originalHour : 24; // If from Wed after 7PM, treat as after 9AM
      
      if (isActuallyThursday && thursdayHour < this.options.thursdayCutoff) {
        // Before 9 AM Thursday = 1 day (Friday)
        daysToAdd = 1;
      } else {
        // After 9 AM Thursday = 5-7 days (next Tuesday - use 5 as the "Fast Delivery By" date)
        daysToAdd = 5;
      }
    }
    // Standard logic based on day of week - OPTIMIZED with array lookup
    else {
      const daysMap = [2, 2, 2, 2, 1, 4, 3]; // [Sun, Mon, Tue, Wed, Thu, Fri, Sat] - FIXED to match requirements
      daysToAdd = daysMap[currentDay];
    }

    // Use effectiveDate for calculations if cutoff was applied
    const baseDate = currentHour >= this.options.standardCutoff && currentDay !== effectiveDate.getDay() 
      ? effectiveDate 
      : currentTimePST;
    
    const deliveryDate = new Date(baseDate);
    deliveryDate.setDate(baseDate.getDate() + daysToAdd);

    return {
      deliveryDate,
      daysToAdd,
      currentDay,
      currentHour
    };
  }

  /**
   * Calculate range delivery dates (Expected Delivery X - Y) - OPTIMIZED
   * Used in cart-drawer.liquid and other range-based displays
   * 
   * CUTOFF: 7 PM PST - orders after this time treated as next day
   * 
   * Logic:
   * - Sun: 2-4 days (Tue-Thu) - Expected Delivery
   * - Mon: 2-4 days (Wed-Fri) - Expected Delivery
   * - Tue: 2-3 days (Thu-Fri) - Expected Delivery
   * - Wed: 2 days (Fri) - Fast Delivery By
   * - Thu: Before 9 AM = 1 day (Fri) - Fast Delivery By, After 9 AM = 5-7 days (next Tue-Thu) - Expected Delivery
   * - Fri: 4-6 days (next Tue-Thu) - Expected Delivery
   * - Sat: 3-5 days (next Tue-Thu) - Expected Delivery
   */
  calculateRangeDelivery() {
    const currentTimePST = this.getCurrentTimePST();
    let currentDay = currentTimePST.getDay();
    let currentHour = currentTimePST.getHours();
    
    // Store original values before cutoff logic
    const originalDay = currentDay;
    const originalHour = currentHour;
    
    // Apply 7 PM PST cutoff - treat orders after 7 PM as next day
    let effectiveDate = new Date(currentTimePST);
    if (currentHour >= this.options.standardCutoff) {
      effectiveDate.setDate(effectiveDate.getDate() + 1);
      currentDay = effectiveDate.getDay();
    }
    
    let startDays = 0;
    let endDays = 0;
    let isOvernightEligible = false;

    // Pre-defined delivery ranges for speed
    const deliveryRanges = [
      [2, 4],   // Sunday: 2-4 days
      [2, 4],   // Monday: 2-4 days
      [2, 3],   // Tuesday: 2-3 days
      [2, 2],   // Wednesday: 2 days (Fast Delivery By)
      [5, 7],   // Thursday: 5-7 days (default, overridden by special logic)
      [4, 6],   // Friday: 4-6 days
      [3, 5]    // Saturday: 3-5 days
    ];

    // For Thursday, we need to check the actual time, not the cutoff-adjusted time
    const isActuallyThursday = originalDay === 4;
    const thursdayHour = isActuallyThursday ? originalHour : 24; // If from Wed after 7PM, treat as after 9AM
    
    if (currentDay === 4 && isActuallyThursday && thursdayHour < this.options.thursdayCutoff) {
      // Before 9 AM Thursday = 1 day (overnight eligible)
      startDays = 1;
      endDays = 1;
      isOvernightEligible = true;
    } else if (currentDay === 4) {
      // After 9 AM Thursday OR Thursday from Wed after 7 PM = 5-7 days
      startDays = 5;
      endDays = 7;
      isOvernightEligible = false;
    } else {
      [startDays, endDays] = deliveryRanges[currentDay];
    }

    // Use effectiveDate for calculations if cutoff was applied
    const baseDate = currentHour >= this.options.standardCutoff && currentDay !== effectiveDate.getDay() 
      ? effectiveDate 
      : currentTimePST;
    
    const startDate = new Date(baseDate);
    const endDate = new Date(baseDate);
    startDate.setDate(baseDate.getDate() + startDays);
    endDate.setDate(baseDate.getDate() + endDays);

    return {
      startDate,
      endDate,
      startDays,
      endDays,
      isOvernightEligible,
      currentDay,
      currentHour
    };
  }

  /**
   * Check if $1 overnight shipping is available - OPTIMIZED
   * Available on Thursday before 9 AM PST ONLY
   * 
   * CUTOFF: 7 PM PST applied first, then Thursday 9 AM PST rule
   * 
   * Logic: Only Thursday before 9 AM PST gets $1 overnight shipping
   */
  isOvernightAvailable() {
    const currentTimePST = this.getCurrentTimePST();
    let currentDay = currentTimePST.getDay();
    let currentHour = currentTimePST.getHours();
    
    // Store original values before cutoff logic
    const originalDay = currentDay;
    const originalHour = currentHour;
    
    // Apply 7 PM PST cutoff - treat orders after 7 PM as next day
    if (currentHour >= this.options.standardCutoff) {
      const effectiveDate = new Date(currentTimePST);
      effectiveDate.setDate(effectiveDate.getDate() + 1);
      currentDay = effectiveDate.getDay();
    }
    
    // Thursday (4) before 9 AM PST ONLY - must actually be Thursday, not Wed after 7 PM
    const isActuallyThursday = originalDay === 4;
    return currentDay === 4 && isActuallyThursday && originalHour < this.options.thursdayCutoff;
  }

  /**
   * Generate default discount banner text based on delivery availability
   */
  generateDefaultBannerText() {
    const isOvernightAvailable = this.isOvernightAvailable();
    const range = this.calculateRangeDelivery();
    
    let bannerText = '';
    
    if (isOvernightAvailable) {
      // Show overnight shipping when available (single day estimate)
      bannerText = `$${this.options.overnightCost} Overnight Shipping | Fast Delivery By ${this.formatDate(range.startDate, 'MM/DD')}`;
    } else {
      // Use the effective day from range calculation (which already handles 7 PM cutoff)
      const effectiveDay = range.currentDay;
      
      if (effectiveDay === 3) { // Wednesday = 2 days
        // Wednesday: Show "Fast Delivery By Friday" (single day estimate)
        bannerText = `Fast Delivery By ${this.formatDate(range.startDate, 'MM/DD')}`;
      } else if (effectiveDay === 4) { // Thursday
        if (range.currentHour < this.options.thursdayCutoff) {
          // Before 9 AM Thursday = 1 day
          bannerText = `Fast Delivery By ${this.formatDate(range.startDate, 'MM/DD')}`;
        } else {
          // After 9 AM Thursday = 5-7 days
          bannerText = `Expected Delivery ${this.formatDate(range.startDate, 'MM/DD')} - ${this.formatDate(range.endDate, 'MM/DD')}`;
        }
      } else {
        // All other days: Show range
        bannerText = `Expected Delivery ${this.formatDate(range.startDate, 'MM/DD')} - ${this.formatDate(range.endDate, 'MM/DD')}`;
      }
    }
    
    return bannerText;
  }

  /**
   * Format date based on configuration - OPTIMIZED with new format
   */
  formatDate(date, format = null) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return 'N/A';
    }

    const formatType = format || this.options.dateFormat;
    
    switch (formatType) {
      case 'MM/DD':
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month}/${day}`;
        
      case 'MM/DD/YYYY':
        const year = date.getFullYear();
        const month2 = (date.getMonth() + 1).toString().padStart(2, '0');
        const day2 = date.getDate().toString().padStart(2, '0');
        return `${month2}/${day2}/${year}`;
        
      case 'ddd, MMMM Do': // NEW: "Tue, August 26th" format
        const dayName = this.daysOfWeek[date.getDay()];
        const monthName = this.months[date.getMonth()];
        const day3 = date.getDate();
        const ordinalSuffix = this.getOrdinalSuffix(day3);
        return `${dayName}, ${monthName} ${day3}${ordinalSuffix}`;
        
      case 'Day, Month DD':
        const dayName2 = this.daysOfWeek[date.getDay()];
        const monthName2 = this.months[date.getMonth()];
        const day4 = date.getDate();
        const ordinalSuffix2 = this.getOrdinalSuffix(day4);
        return `${dayName2}, ${monthName2} ${day4}${ordinalSuffix2}`;
        
      case 'Month DD':
        const monthName3 = this.months[date.getMonth()];
        const day5 = date.getDate();
        const ordinalSuffix3 = this.getOrdinalSuffix(day5);
        return `${monthName3} ${day5}${ordinalSuffix3}`;
        
      default:
        return date.toLocaleDateString();
    }
  }

  /**
   * Get ordinal suffix for dates - OPTIMIZED
   */
  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /**
   * Calculate countdown to cutoff - OPTIMIZED
   */
  calculateCountdown() {
    const currentTimePST = this.getCurrentTimePST();
    const currentHour = currentTimePST.getHours();
    const cutoffHour = this.options.standardCutoff;
    
    const cutoffTimePST = new Date(currentTimePST);
    if (currentHour >= cutoffHour) {
      cutoffTimePST.setDate(cutoffTimePST.getDate() + 1);
    }
    cutoffTimePST.setHours(cutoffHour, 0, 0, 0);
    
    const timeUntilCutoff = cutoffTimePST.getTime() - currentTimePST.getTime();
    
    if (timeUntilCutoff <= 0) {
      return { hours: 0, minutes: 0, total: 0 };
    }
    
    const hours = Math.floor(timeUntilCutoff / (60 * 60 * 1000));
    const minutes = Math.floor((timeUntilCutoff % (60 * 60 * 1000)) / (60 * 1000));
    
    return { hours, minutes, total: timeUntilCutoff };
  }

  /**
   * Format countdown as readable string - OPTIMIZED
   */
  formatCountdown(hours, minutes) {
    if (hours > 0) {
      return `${hours} hr${hours > 1 ? 's' : ''}${minutes > 0 ? `, ${minutes} min` : ''}`;
    }
    return `${minutes} min`;
  }

  /**
   * Generate HTML for delivery display - OPTIMIZED
   */
  generateDeliveryHTML(type = 'standard', options = {}) {
    const defaultOptions = {
      showCountdown: this.options.showCountdown,
      showOvernight: this.options.showOvernight,
      customStyles: '',
      ...options
    };

    switch (type) {
      case 'standard':
        return this.generateStandardHTML(defaultOptions);
      case 'range':
        return this.generateRangeHTML(defaultOptions);
      case 'overnight':
        return this.generateOvernightHTML(defaultOptions);
      default:
        return this.generateStandardHTML(defaultOptions);
    }
  }

  /**
   * Generate standard delivery HTML (Fast Delivery By) - OPTIMIZED
   */
  generateStandardHTML(options) {
    const delivery = this.calculateStandardDelivery();
    const countdown = this.calculateCountdown();
    const formattedDate = this.formatDate(delivery.deliveryDate);
    
    let html = `
      <div style="border: 1px solid #2AC5A0; background-color: #F6FFFA; padding: 15px; border-radius: 8px; ${options.customStyles || ''}">
        <span>✅ <span style="color: #3f8b33;">FAST delivery</span> 
          <span style="font-weight: bold;">${formattedDate}</span>
        </span>
    `;
    
    if (options.showCountdown && countdown.total > 0 && countdown.total < 24 * 60 * 60 * 1000) {
      const countdownStr = this.formatCountdown(countdown.hours, countdown.minutes);
      html += `<br><span>Order in <span style="color: #3f8b33; font-weight: bold;">${countdownStr}</span>.</span>`;
    }
    
    html += '</div>';
    return html;
  }

  /**
   * Generate range delivery HTML (Expected Delivery X - Y) - OPTIMIZED
   */
  generateRangeHTML(options) {
    const range = this.calculateRangeDelivery();
    const startFormatted = this.formatDate(range.startDate, 'MM/DD');
    const endFormatted = this.formatDate(range.endDate, 'MM/DD');
    
    let html = `
      <div style="display: flex; align-items: center; gap: 4px;">
        <span>Expected delivery</span>
        <span>${startFormatted} <span class="date-dash">-</span> ${endFormatted}</span>
      </div>
    `;
    
    if (options.showOvernight && range.isOvernightEligible) {
      html = `
        <div style="display: flex; align-items: center; gap: 4px;">
          <span>$${this.options.overnightCost} Overnight Delivery:</span>
          <span>${startFormatted}</span>
        </div>
      `;
    }
    
    return html;
  }

  /**
   * Generate overnight delivery HTML - OPTIMIZED
   */
  generateOvernightHTML(options) {
    const range = this.calculateRangeDelivery();
    if (!range.isOvernightEligible) {
      return this.generateRangeHTML(options);
    }
    
    const startFormatted = this.formatDate(range.startDate, 'MM/DD');
    return `
      <div style="display: flex; align-items: center; gap: 4px;">
        <span>$${this.options.overnightCost} Overnight Delivery:</span>
        <span>${startFormatted}</span>
      </div>
    `;
  }

  /**
   * Update DOM element with delivery information - OPTIMIZED
   */
  updateDeliveryElement(elementId, type = 'standard', options = {}) {
    const element = document.getElementById(elementId);
    if (!element) {
      console.warn(`Delivery element with ID '${elementId}' not found`);
      return;
    }
    
    const html = this.generateDeliveryHTML(type, options);
    element.innerHTML = html;
  }

  /**
   * Get delivery information as data object - OPTIMIZED
   */
  getDeliveryInfo(type = 'standard') {
    switch (type) {
      case 'standard':
        return this.calculateStandardDelivery();
      case 'range':
        return this.calculateRangeDelivery();
      default:
        return this.calculateStandardDelivery();
    }
  }

  /**
   * Create handler instance for specific template type - OPTIMIZED
   */
  static createForTemplate(templateType, options = {}) {
    return new DeliveryDateHandler({
      templateType,
      ...options
    });
  }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryDateHandler;
} else if (typeof window !== 'undefined') {
  window.DeliveryDateHandler = DeliveryDateHandler;
}

// Auto-initialize if script is loaded directly - OPTIMIZED
if (typeof document !== 'undefined') {
  // Create global instance immediately for instant access
  window.deliveryHandler = new DeliveryDateHandler({
    templateType: 'main-product' // Use main-product template settings
  });
  
  // Dispatch custom event for other scripts to hook into
  document.dispatchEvent(new CustomEvent('deliveryHandlerReady', {
    detail: { handler: window.deliveryHandler }
  }));
}
